// Simple translation cache to avoid redundant API calls
const translationCache = {};

/**
 * Translates text using MyMemory Translation API (free, no key required)
 * @param {string} text - Text to translate
 * @param {string} targetLang - Target language code (en, ru, ka)
 * @returns {Promise<string>} - Translated text
 */
export async function translateText(text, targetLang) {
  // If target is Georgian (original language), return as-is
  if (targetLang === 'ka') {
    return text;
  }

  // Check cache first
  const cacheKey = `${text}-${targetLang}`;
  if (translationCache[cacheKey]) {
    return translationCache[cacheKey];
  }

  try {
    // MyMemory API supports ka (Georgian) -> en/ru
    const sourceLang = 'ka';
    const apiUrl = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${sourceLang}|${targetLang}`;
    
    const response = await fetch(apiUrl);
    const data = await response.json();
    
    if (data.responseData && data.responseData.translatedText) {
      const translated = data.responseData.translatedText;
      translationCache[cacheKey] = translated;
      return translated;
    }
    
    // Fallback to original text if translation fails
    return text;
  } catch (error) {
    console.error('Translation error:', error);
    return text; // Return original text on error
  }
}

/**
 * Translates post content based on language
 * @param {object} post - Post object with title and content
 * @param {string} language - Target language
 * @returns {Promise<object>} - Post with translated content
 */
export async function translatePost(post, language) {
  if (language === 'ka') {
    return post; // Return original Georgian post
  }

  // Check if translations are already stored in the post
  if (post.translations && post.translations[language]) {
    return {
      ...post,
      title: post.translations[language].title,
      content: post.translations[language].content
    };
  }

  // Translate on-the-fly
  try {
    const [translatedTitle, translatedContent] = await Promise.all([
      translateText(post.title, language),
      translateText(post.content, language)
    ]);

    return {
      ...post,
      title: translatedTitle,
      content: translatedContent
    };
  } catch (error) {
    console.error('Error translating post:', error);
    return post; // Return original on error
  }
}
